import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, XCircle, Sparkles } from 'lucide-react';
type PlanId = 'completo' | 'pro';
const PLANS: Record<PlanId, {
  id: PlanId;
  name: string;
  price: string;
  oldPrice: string;
  url: string;
  features: {
    text: string;
    included: boolean;
    bold?: boolean;
  }[];
  badge?: string;
  highlight?: string;
}> = {
  completo: {
    id: 'completo',
    name: 'PLAN COMPLETO',
    price: '$19.990',
    oldPrice: '$59.900',
    url: 'https://checkout.complementodigital.shop/cart/40771159851137:1',
    features: [{
      text: '6 Fases',
      included: true
    }, {
      text: '6 Bonus',
      included: true
    }, {
      text: 'Sin Bonus Plus Premium',
      included: false
    }]
  },
  pro: {
    id: 'pro',
    name: 'KIT PRO',
    price: '$24.990',
    oldPrice: '$59.900',
    url: 'https://checkout.complementodigital.shop/cart/40772030529665:1',
    badge: '🔥 MÁS ELEGIDO',
    highlight: '⚡ Por solo $5.000 más obtenés los 3 Bonus Plus Premium',
    features: [{
      text: '6 Fases',
      included: true
    }, {
      text: '6 Bonus',
      included: true
    }, {
      text: '3 Bonus Plus Premium',
      included: true,
      bold: true
    }, {
      text: 'Acceso a todos los recursos',
      included: true
    }]
  }
};
interface PlanSelectorProps {
  variant?: 'light' | 'dark';
  ctaLabel?: string;
}
export function PlanSelector({
  variant = 'light',
  ctaLabel = 'QUIERO RECUPERAR EL CONTROL DE MI DINERO'
}: PlanSelectorProps) {
  const [selected, setSelected] = useState<PlanId>('pro');
  const isDark = variant === 'dark';
  const selectedPlan = PLANS[selected];
  return <div className="w-full max-w-4xl mx-auto" data-id="element-316">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" data-id="element-317">
        {(Object.values(PLANS) as (typeof PLANS)[PlanId][]).map(plan => {
        const isSelected = selected === plan.id;
        const isPro = plan.id === 'pro';
        return <button key={plan.id} type="button" onClick={() => setSelected(plan.id)} aria-pressed={isSelected} className={`relative text-left rounded-2xl p-5 border-2 transition-all duration-200 focus:outline-none focus-visible:ring-4 focus-visible:ring-success/40 ${isSelected ? isPro ? 'bg-navy-900 border-success shadow-[0_0_30px_rgba(16,185,129,0.35)] scale-[1.02]' : `${isDark ? 'bg-navy-800' : 'bg-white'} border-success shadow-lg scale-[1.02]` : isPro ? `${isDark ? 'bg-navy-800/60' : 'bg-navy-50'} border-transparent opacity-80 hover:opacity-100` : `${isDark ? 'bg-navy-800/60' : 'bg-white'} border-slate-200 opacity-80 hover:opacity-100`}`} data-id="element-318">
              {/* Badge */}
              {plan.badge && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-success text-white px-3 py-0.5 rounded-full text-[11px] font-bold whitespace-nowrap shadow-md" data-id="element-319">
                  {plan.badge}
                </div>}

              {/* Radio + Title */}
              <div className="flex items-start gap-3 mb-3" data-id="element-320">
                <div className={`mt-1 w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${isSelected ? 'border-success bg-success' : isDark || isPro ? 'border-slate-500' : 'border-slate-300'}`} data-id="element-321">
                  {isSelected && <div className="w-2 h-2 rounded-full bg-white" data-id="element-322"></div>}
                </div>
                <div className="flex-1 min-w-0" data-id="element-323">
                  <h3 className={`text-base md:text-lg font-extrabold leading-tight ${isPro ? 'text-white' : isDark ? 'text-white' : 'text-navy-900'}`} data-id="element-324">
                    {plan.name}
                  </h3>
                  <div className="flex items-baseline gap-2 mt-1 flex-wrap" data-id="element-325">
                    <span className={`text-2xl md:text-3xl font-extrabold ${isPro && isSelected ? 'text-success' : isPro ? 'text-white' : isDark ? 'text-white' : 'text-navy-900'}`} data-id="element-326">
                      {plan.price}
                    </span>
                    <span className={`text-xs line-through ${isPro || isDark ? 'text-slate-400' : 'text-slate-400'}`} data-id="element-327">
                      {plan.oldPrice}
                    </span>
                    <span className={`text-[11px] font-semibold ${isPro || isDark ? 'text-slate-300' : 'text-slate-500'}`} data-id="element-328">
                      ARS
                    </span>
                  </div>
                </div>
              </div>

              {/* Features */}
              <ul className="space-y-1.5 mb-2" data-id="element-329">
                {plan.features.map((f, i) => <li key={i} className="flex items-center gap-2 text-sm" data-id="element-330">
                    {f.included ? <CheckCircle2 className="w-4 h-4 text-success flex-shrink-0" data-id="element-331" /> : <XCircle className="w-4 h-4 text-slate-400 flex-shrink-0" data-id="element-332" />}
                    <span className={`${f.included ? f.bold ? isPro ? 'text-white font-bold' : isDark ? 'text-white font-bold' : 'text-navy-900 font-bold' : isPro || isDark ? 'text-slate-200' : 'text-slate-700' : 'text-slate-400 line-through'}`} data-id="element-333">
                      {f.text}
                    </span>
                  </li>)}
              </ul>

              {plan.highlight && <p className="text-[11px] font-semibold text-success mt-2 leading-snug" data-id="element-334">
                  {plan.highlight}
                </p>}
            </button>;
      })}
      </div>

      {/* Shared CTA */}
      <motion.a href={selectedPlan.url} target="_blank" rel="noopener noreferrer" className="block w-full bg-success hover:bg-success-hover text-white font-black text-base md:text-xl py-4 md:py-5 px-6 rounded-xl shadow-[0_0_25px_rgba(16,185,129,0.45)] transition-all text-center" whileHover={{
      scale: 1.02
    }} whileTap={{
      scale: 0.98
    }} key={selectedPlan.id} data-id="element-335">
        <span className="flex items-center justify-center gap-2 flex-wrap" data-id="element-336">
          <Sparkles className="w-5 h-5" data-id="element-337" />
          {ctaLabel} — {selectedPlan.price} ARS
        </span>
      </motion.a>

      <p className={`text-center text-xs mt-3 ${isDark ? 'text-slate-400' : 'text-slate-500'}`} data-id="element-338">
        Acceso inmediato • Pago 100% seguro • Garantía de satisfacción
      </p>
    </div>;
}